import React, { useEffect, useState } from "react";
import AgeCompare from "./AgeCompare";
import AgeGen from "./AgeGen";
import Agingpace from "./Agingpace";
import Glucose from "./Glucose";
import HealthJourney from "./HealthJourney";
import SleepScore from "./SleepScore";
import HealthTimeline from "./HealthTimeline";
import Highchart from "./Highchart";
import LifestylePyramid from "./LifestylePyramid";
import Nutrition from "./Nutrition";
import MyGoqii from "./MyGoqii";
import Safebar from "./Safebar";
import Stepgraph from "./Stepgraph";
import WeightChart from "./WeightChart";
import Agecard from "./Agecard";
import TaskPage from "./TasksPage";
import InsuranceCards from "./InsuranceCards";
import CaloriesCard from "./CaloriesCard";
import { lifestyleApi } from "../apiService/api";
import { triggerShare } from "../utils/Utils";

// Maps URL path segment (from web-card url field) → component factory
// Matches the route definitions in AppRoute.jsx
const URL_PATH_MAP = {
  agecard:             (cfg) => <Agecard shareConfig={cfg} />,
  graph_v1:            (cfg) => <HealthJourney shareConfig={cfg} />,
  agingpace:           (cfg) => <Agingpace shareConfig={cfg} />,
  safebar:             (cfg) => <Safebar shareConfig={cfg} />,
  lifestylecomparison: (cfg) => <MyGoqii shareConfig={cfg} />,
  lifestylepyramid:    (cfg) => <LifestylePyramid shareConfig={cfg} />,
  healthtimeline:      (cfg) => <HealthTimeline shareConfig={cfg} />,
  stepgraph:           (cfg) => <Stepgraph shareConfig={cfg} />,
  weightzone:          (cfg) => <WeightChart shareConfig={cfg} />,
  hra:                 (cfg) => <Highchart shareConfig={cfg} />,
  insurancecard:       ()    => <InsuranceCards />,
  "weekly-calories":   (cfg) => <CaloriesCard shareConfig={cfg} />,
  "age-reversor-compare": (cfg, onHide) => <AgeCompare onHide={onHide} />,
  "age-reversor-gen":     (cfg, onHide) => <AgeGen onHide={onHide} />,
};

// familyVitals cards have no url — map by keyword
const VITALS_KEYWORD_MAP = {
  "Lifestyle-tasklist":  (cfg) => <TaskPage type="dailyTasks"   shareConfig={cfg} />,
  "Graph_weekly_progress": (cfg) => <TaskPage type="weekProgress" shareConfig={cfg} />,
};

const getUrlPathSegment = (url) => {
  if (!url) return null;
  try {
    const pathname = new URL(url).pathname;
    return pathname.split("/").filter(Boolean)[0] || null;
  } catch {
    // url may be relative or malformed
    return url.split("/").filter(Boolean)[0] || null;
  }
};

const buildShareConfig = (card) => {
  const d = card?.cardData?.[0]?.data;
  if (!d) return null;
  const isSharable = d.isSharable ?? d.shareable ?? false;
  const text = d.sharedText || d.shareText || "";
  return { isSharable: !!isSharable, text };
};


const RewardsBanner = ({ card }) => {
  const d = card?.cardData?.[0]?.data;
  if (!d?.imageUrl) return null;
  return (
    <img
      src={d.imageUrl}
      alt={d.displayName || ""}
      style={{ width: "100%", display: "block", borderRadius: "12px" }}
      onClick={() => {
        if (d.onTap?.FAI?.url) window.location.href = d.onTap.FAI.url;
      }}
    />
  );
};

const ShareOverlay = ({ shareConfig }) => {
  if (!shareConfig?.isSharable) return null;
  return (
    <div
      onClick={() => triggerShare(shareConfig.text)}
      style={{
        position: "absolute",
        top: 14,
        right: 32,
        width: 26,
        height: 26,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        color: "#666666",
        zIndex: 10,
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path
          d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 5.12548 15.0077 5.24919 15.0227 5.37063L8.08261 9.19726C7.54305 8.46808 6.6635 8 5.66667 8C4.01 8 2.66667 9.34315 2.66667 11C2.66667 12.6569 4.01 14 5.66667 14C6.6635 14 7.54305 13.5319 8.08261 12.8027L15.0227 16.6294C15.0077 16.7508 15 16.8745 15 17C15 18.6569 16.3431 20 18 20C19.6569 20 21 18.6569 21 17C21 15.3431 19.6569 14 18 14C16.9786 14 16.0784 14.5144 15.5022 15.3027L8.5625 11.476C8.5743 11.3222 8.58 11.1621 8.58 11C8.58 10.8379 8.5743 10.6778 8.5625 10.524L15.5022 6.69726C16.0784 7.48558 16.9786 8 18 8Z"
          fill="currentColor"
        />
      </svg>
    </div>
  );
};

const resolveCard = (card) => {
  const d = card?.cardData?.[0]?.data;
  const itemType = d?.itemType || card?.itemType;

  if (itemType === "image") {
    return { renderFn: null, isImage: true };
  }

  if (itemType === "familyVitals") {
    const fn = VITALS_KEYWORD_MAP[card.keyword];
    return { renderFn: fn || null, isImage: false };
  }

  // web-card: use url path segment
  const segment = getUrlPathSegment(d?.url);
  const fn = segment ? URL_PATH_MAP[segment] : null;

  // Agecard has no SectionHeader — needs overlay share
  const needsShareOverlay = segment === "agecard";

  return { renderFn: fn || null, isImage: false, needsShareOverlay };
};

const wrapperStyle = (isFirst) => ({
  marginTop: isFirst ? "10px" : undefined,
  marginBottom: "10px",
  paddingLeft: "16px",
  paddingRight: "16px",
});

const AllPages = () => {
  const [cards, setCards] = useState(null);
  const [showAgeCompare, setShowAgeCompare] = useState(true);
  const [showAgeGen, setShowAgeGen] = useState(true);

  useEffect(() => {
    lifestyleApi
      .fetchPageByFeatures(1, 666)
      .then((res) => {
        if (res?.data?.code === 200 && Array.isArray(res.data.data?.cards)) {
          setCards(res.data.data.cards);
        } else {
          setCards([]);
        }
      })
      .catch(() => setCards([]));
  }, []);

  if (cards === null) return null;

  console.log("AllPages cards:", cards);

  if (cards.length === 0) {
    return (
      <div style={{ width: "100%", boxSizing: "border-box" }}>
        
        <div style={wrapperStyle(true)}><InsuranceCards /></div>
      
        {showAgeCompare && (
          <div style={wrapperStyle()}>
            <AgeCompare onHide={() => setShowAgeCompare(false)} />
          </div>
        )}
        {/* <div style={wrapperStyle()}><Agingpace /></div> */}
        <div style={wrapperStyle()}><TaskPage type="dailyTasks" /></div>
        
        <div style={wrapperStyle()}><Agingpace /></div>
        {/* <div style={wrapperStyle()}><Glucose /></div> */}
        <div style={wrapperStyle()}><SleepScore /></div>
        <div style={wrapperStyle()}><HealthJourney /></div>
        <div style={wrapperStyle()}><Highchart /></div>
        <div style={wrapperStyle()}><LifestylePyramid /></div>
        <div style={wrapperStyle()}><Nutrition /></div>
        <div style={wrapperStyle()}><MyGoqii /></div>
        <div style={wrapperStyle()}><Safebar /></div>
        <div style={wrapperStyle()}><TaskPage type="weekProgress" /></div>
        <div style={wrapperStyle()}><Stepgraph /></div>
        <div style={wrapperStyle()}><WeightChart /></div>
        {showAgeGen && (
          <div style={wrapperStyle()}>
            <AgeGen onHide={() => setShowAgeGen(false)} />
          </div>
        )}
        {/* <div style={wrapperStyle(true)}><Agecard /></div> */}
        <div style={wrapperStyle()}><HealthTimeline /></div>
      </div>
    );
  }

  // Build the list of renderable API cards first (dropping unrecognized
  // ones), so we know exactly which card ends up visually first and where
  // the HealthJourney (graph_v1) card lands, if any.
  const renderableCards = cards
    .map((card, idx) => {
      const { renderFn, isImage, needsShareOverlay } = resolveCard(card);
      if (!isImage && !renderFn) return null;
      return {
        card,
        idx,
        renderFn,
        isImage,
        needsShareOverlay,
        segment: getUrlPathSegment(card?.cardData?.[0]?.data?.url),
      };
    })
    .filter(Boolean);

  // SleepScore is a locally-added card, not returned by the API. Show it
  // right above the HealthJourney (graph_v1) card if one is present;
  // otherwise show it as the first card so it's always visible.
  const graphV1Pos = renderableCards.findIndex((c) => c.segment === "graph_v1");
  const sleepScoreBeforePos = graphV1Pos !== -1 ? graphV1Pos : 0;

  return (
    <div style={{ width: "100%", boxSizing: "border-box" }}>
      {renderableCards.map((c, pos) => {
        if (c.segment === "age-reversor-compare" && !showAgeCompare) return null;
        if (c.segment === "age-reversor-gen" && !showAgeGen) return null;

        const shareConfig = buildShareConfig(c.card);
        const isVisuallyFirst = pos === 0 && sleepScoreBeforePos !== 0;
        const onHide =
          c.segment === "age-reversor-compare"
            ? () => setShowAgeCompare(false)
            : c.segment === "age-reversor-gen"
            ? () => setShowAgeGen(false)
            : undefined;

        return (
          <React.Fragment key={`${c.card.keyword}-${c.idx}`}>
            {pos === sleepScoreBeforePos && (
              <div style={wrapperStyle(sleepScoreBeforePos === 0)}><SleepScore /></div>
            )}
            <div style={{ ...wrapperStyle(isVisuallyFirst), position: "relative" }}>
              {c.isImage ? <RewardsBanner card={c.card} /> : c.renderFn(shareConfig, onHide)}
              {c.needsShareOverlay && <ShareOverlay shareConfig={shareConfig} />}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default AllPages;
